import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { AuthApi } from '../../services/authApi';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate email
    if (!email) {
      setError('Vui lòng nhập email');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Email không hợp lệ');
      return;
    }

    setIsLoading(true);

    try {
      await AuthApi.forgotPassword(email);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Không thể gửi email. Vui lòng thử lại sau.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess(false);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-eh bg-eh-cream p-6 text-left align-middle shadow-eh-soft ring-1 ring-eh-border transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-m2-on-surface"
                  >
                    Quên mật khẩu
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-m2-outline hover:text-m2-on-surface-variant transition-colors"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {!success ? (
                  <form onSubmit={handleSubmit}>
                    <div className="mt-2">
                      <p className="text-sm text-m2-on-surface-variant mb-4">
                        Nhập địa chỉ email của bạn và chúng tôi sẽ gửi mật khẩu mới cho bạn.
                      </p>

                      <div>
                        <label htmlFor="reset-email" className="block text-sm font-medium text-m2-on-surface mb-2">
                          Email
                        </label>
                        <div className="relative">
                          <input
                            id="reset-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="appearance-none block w-full pl-10 pr-3 py-2 border border-m2-outline rounded-md placeholder-m2-outline focus:outline-none focus:ring-m2-primary focus:border-m2-primary sm:text-sm"
                            placeholder="Nhập email của bạn"
                            disabled={isLoading}
                          />
                          <EnvelopeIcon className="h-5 w-5 text-m2-outline absolute left-3 top-2.5" />
                        </div>
                      </div>

                      {error && (
                        <div className="mt-3 rounded-md bg-m2-error/10 p-3">
                          <p className="text-sm text-m2-error">{error}</p>
                        </div>
                      )}
                    </div>

                    <div className="mt-6 flex gap-3">
                      <button
                        type="button"
                        className="flex-1 inline-flex justify-center rounded-md border border-m2-outline bg-m2-surface-container px-4 py-2 text-sm font-medium text-m2-on-surface hover:bg-m2-surface-low focus:outline-none focus:ring-2 focus:ring-m2-primary focus:ring-offset-2"
                        onClick={handleClose}
                        disabled={isLoading}
                      >
                        Hủy
                      </button>
                      <button
                        type="submit"
                        className="flex-1 inline-flex justify-center rounded-eh-sm border border-transparent bg-eh-forest-ink px-4 py-2 text-sm font-medium text-eh-cream hover:bg-eh-forest-shadow focus:outline-none focus:ring-2 focus:ring-eh-forest-ink focus:ring-offset-2 disabled:bg-m2-outline disabled:cursor-not-allowed"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Đang gửi...
                          </>
                        ) : (
                          'Gửi mật khẩu mới'
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="mt-2">
                    <div className="flex flex-col items-center text-center py-4">
                      <CheckCircleIcon className="h-16 w-16 text-green-500 mb-4" />
                      <h4 className="text-lg font-semibold text-m2-on-surface mb-2">
                        Email đã được gửi!
                      </h4>
                      <p className="text-sm text-m2-on-surface-variant mb-4">
                        Mật khẩu mới đã được gửi đến email <strong>{email}</strong>.
                        Vui lòng kiểm tra hộp thư đến và spam.
                      </p>
                      <button
                        type="button"
                        className="w-full inline-flex justify-center rounded-eh-sm border border-transparent bg-eh-forest-ink px-4 py-2 text-sm font-medium text-eh-cream hover:bg-eh-forest-shadow focus:outline-none focus:ring-2 focus:ring-eh-forest-ink focus:ring-offset-2"
                        onClick={handleClose}
                      >
                        Đóng
                      </button>
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ForgotPasswordModal;
